import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Select,
  DatePicker,
  message,
  Typography,
  Avatar,
  Spin,
  Divider,
  Space
} from 'antd';
import { SaveOutlined, UndoOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import videoService from '../../../services/videoService';
import masterService from '../../../services/masterService';
import { useUser } from '../../../context/UserContext';
import moment from 'moment';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const AddShow = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [form] = Form.useForm();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  
  // Dropdown data
  const [genres, setGenres] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        setLoading(true);
        
        const [genresRes, ratingsRes, languagesRes, tagsRes] = await Promise.all([
          masterService.getGenres(),
          masterService.getVideoRatings(),
          masterService.getVideoLanguages(),
          masterService.getTagList()
        ]);

        if (genresRes.data?.status) {
          setGenres(genresRes.data.data || []);
        }
        if (ratingsRes.data?.status) {
          setRatings(ratingsRes.data.data || []);
        }
        if (languagesRes.data?.status) {
          setLanguages(languagesRes.data.data || []);
        }
        if (tagsRes.data?.status) {
          setTags(tagsRes.data.data || []);
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        message.error('Failed to load form data');
      } finally {
        setLoading(false);
      }
    };

    loadDropdownData();
  }, []);

  // Handle form submission
  const handleSubmit = async (values) => {
    try {
      setSpinning(true);
      
      // Format dates
      const formattedValues = {
        ...values,
        vs_original_release_date: values.vs_original_release_date 
          ? moment(values.vs_original_release_date).format('YYYY-MM-DD HH:mm:ss')
          : null,
        vs_release_date: values.vs_release_date 
          ? moment(values.vs_release_date).format('YYYY-MM-DD HH:mm:ss')
          : null,
        genre_id: Array.isArray(values.genre_list) ? values.genre_list.join(',') : values.genre_list
      };

      console.log('Submitting show data:', formattedValues);
      const response = await videoService.addVideoShow(formattedValues);
      
      if (response.data && response.data.status) {
        message.success(response.data.message || 'Show added successfully');
        form.resetFields();
        navigate('/Video/Show');
      } else {
        message.error(response.data.message || 'Failed to add show');
      }
    } catch (error) {
      console.error('Error adding show:', error);
      message.error('Failed to add show');
    } finally {
      setSpinning(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    form.resetFields();
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/Video/Show');
  };

  // Only allow numbers
  const onlyNumber = (e) => {
    const charCode = e.which ? e.which : e.keyCode;
    if (
      charCode !== 8 && // backspace
      charCode !== 46 && // delete
      charCode !== 37 && // left arrow
      charCode !== 39 && // right arrow
      (charCode < 48 || charCode > 57)
    ) {
      e.preventDefault();
    }
  };

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Add New Video Show</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Add Video Show"
      desc="Create a new video show with all details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Spin spinning={spinning}>
        <Card>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={{
              is_subscription_required: 'N',
              video_show_is_active: 'Y',
              vs_runtime: 0,
              vs_endtime: 0
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="video_show_title"
                label="Movie Title"
                rules={[{ required: true, message: 'Please enter movie title' }]}
              >
                <Input placeholder="Enter movie title" />
              </Form.Item>

              <Form.Item
                name="director"
                label="Director"
                rules={[{ required: true, message: 'Please enter director name' }]}
              >
                <Input placeholder="Enter director name" />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="cast_members"
                label="Cast Members"
                rules={[{ required: true, message: 'Please enter cast members' }]}
              >
                <Input placeholder="Enter cast members" />
              </Form.Item>

              <Form.Item
                name="rating_description"
                label="Rating Description"
              >
                <Input placeholder="Enter rating description" />
              </Form.Item>
            </div>

            <Form.Item
              name="vs_description"
              label="Description"
              rules={[{ required: true, message: 'Please enter description' }]}
            >
              <TextArea 
                rows={4} 
                placeholder="Enter movie description"
                maxLength={2000}
                showCount
              />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="vs_runtime"
                label="Runtime in minutes"
                rules={[{ required: true, message: 'Please enter runtime' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter runtime in minutes"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>

              <Form.Item
                name="vs_endtime"
                label="End in minutes"
                rules={[{ required: true, message: 'Please enter end time' }]}
              >
                <Input 
                  type="number" 
                  placeholder="Enter end time in minutes"
                  onKeyPress={onlyNumber}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="vs_original_release_date"
                label="Original Release Date"
              >
                <DatePicker 
                  showTime 
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Select original release date"
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name="vs_release_date"
                label="Release Date"
              >
                <DatePicker 
                  showTime 
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Select release date"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="genre_list"
                label="Movie Genre"
                rules={[{ required: true, message: 'Please select genre' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="Select genres"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {genres.map(genre => (
                    <Option key={genre.genre_id} value={genre.genre_id}>
                      {genre.genre_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="tags_list"
                label="Tags"
              >
                <Select
                  mode="multiple"
                  placeholder="Select tags"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {tags.map(tag => (
                    <Option key={tag.tag_id} value={tag.tag_id}>
                      {tag.tag_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <Form.Item
                name="is_subscription_required"
                label="Is Subscription Required"
                rules={[{ required: true, message: 'Please select subscription requirement' }]}
              >
                <Select placeholder="Select subscription requirement">
                  <Option value="Y">Yes</Option>
                  <Option value="N">No</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="rating_id"
                label="Rating"
                rules={[{ required: true, message: 'Please select rating' }]}
              >
                <Select
                  placeholder="Select rating"
                  loading={loading}
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {ratings.map(rating => (
                    <Option key={rating.vr_id} value={rating.vr_id}>
                      {rating.rating_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="video_show_is_active"
              label="Status"
              rules={[{ required: true, message: 'Please select status' }]}
            >
              <Select placeholder="Select status">
                <Option value="Y">Active</Option>
                <Option value="N">Inactive</Option>
              </Select>
            </Form.Item>

            <Divider />

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SaveOutlined />}
                  loading={spinning}
                  size="large"
                >
                  Add Movie
                </Button>
                <Button 
                  onClick={handleReset}
                  icon={<UndoOutlined />}
                  size="large"
                >
                  Reset
                </Button>
                <Button 
                  onClick={handleBack}
                  icon={<ArrowLeftOutlined />}
                  size="large"
                >
                  Go Back
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </Spin>
    </PageLayout>
  );
};

export default AddShow;
